import { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { centrifugeService } from "../axiosBase/centrifugeService";
import {
  setWsConnectionStatus,
  setSingleWsEvent,
} from "../store/modules/websocket";
import { setWsCredentials } from "../store/modules/profile";
import { getRealtimeWsToken } from "../services/realtime";

/**
 * Core WebSocket lifecycle hook — mount once via WebSocketConnectHelper.
 *
 * Behaviour:
 *   1. Waits for the user to be authenticated (accessToken present in Redux).
 *   2. If wsCredentials were already set during login, uses them directly.
 *      Otherwise (page refresh), calls GET /realtime/ws/token/ to fetch fresh
 *      credentials, then connects.
 *   3. On every Centrifuge publication, routes data.type → Redux wsEvents via
 *      setSingleWsEvent so any component can react with useWebSocketEventHandler.
 *   4. Disconnects and cleans up when the user logs out (accessToken → null).
 *
 * Components should NOT call this hook themselves. Use instead:
 *   - useWebSocketEventHandler() — read / clear typed events from Redux
 *   - useCentrifugeChannel(channel) — subscribe to a specific channel
 */
export const useWebSocketManager = () => {
  const dispatch = useDispatch();
  const { accessToken, wsCredentials } = useSelector(
    (state) => state.profileDetailsReducer,
  );

  // Prevent double-init when accessToken changes due to silent refresh cycles.
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!accessToken) {
      // Logout — tear down everything
      if (initializedRef.current) {
        centrifugeService.disconnect();
        dispatch(setWsConnectionStatus("disconnected"));
        initializedRef.current = false;
      }
      return;
    }

    if (initializedRef.current) return; // Already connected for this session
    initializedRef.current = true;

    let active = true;
    let cleanupConn = null;
    let cleanupPub = null;

    const init = async () => {
      let { token: wsToken, socketUrl } = wsCredentials;

      // Page refresh path: wsCredentials are empty, fetch from server
      if (!wsToken || !socketUrl) {
        try {
          const res = await getRealtimeWsToken();
          wsToken = res.data.data.token;
          socketUrl = res.data.data.socket_url;
          if (active) dispatch(setWsCredentials({ token: wsToken, socketUrl }));
        } catch (err) {
          console.error("[WebSocketManager] Failed to fetch WS token:", err);
          initializedRef.current = false;
          return;
        }
      }

      if (!active) return;

      centrifugeService.connect({ token: wsToken, socketUrl });

      cleanupConn = centrifugeService.onConnectionChange({
        onConnecting: () => dispatch(setWsConnectionStatus("connecting")),
        onConnected: () => dispatch(setWsConnectionStatus("connected")),
        onDisconnected: () => dispatch(setWsConnectionStatus("disconnected")),
      });

      // Route all channel publications into Redux so any component can react
      // by reading useWebSocketEventHandler().wsEvents[eventType]
      cleanupPub = centrifugeService.onPublication((channel, data) => {
        // Server wraps the event: { event_type: { type, payload } }
        // Unwrap to the inner object if data.type is not directly present.
        const eventData = data?.type ? data : Object.values(data ?? {})[0];
        if (eventData?.type) {
          dispatch(
            setSingleWsEvent({ eventType: eventData.type, data: eventData }),
          );
        }
      });
    };

    init();

    return () => {
      active = false;
      cleanupConn?.();
      cleanupPub?.();
      centrifugeService.disconnect();
      dispatch(setWsConnectionStatus("disconnected"));
      initializedRef.current = false;
    };
  }, [accessToken]); // eslint-disable-line react-hooks/exhaustive-deps
};
